import { createContext, useContext, useState, useEffect } from "react";
import { authAPI, usersAPI, handleAPIError } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem("user");
        if (savedUser) {
          const userData = JSON.parse(savedUser);

          // Verify token is still valid by getting current user
          const response = await authAPI.getCurrentUser();
          if (response.success) {
            setUser(response.data.user);

            // Update localStorage with fresh data
            localStorage.setItem(
              "user",
              JSON.stringify({
                ...response.data.user,
                token: userData.token,
              })
            );
          } else {
            // Token invalid, clear localStorage
            localStorage.removeItem("user");
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.removeItem("user");
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Load users for admin/normal users
  useEffect(() => {
    const loadUsers = async () => {
      // Usuário sem acesso ao Hub (ex.: conta só do Monitor 360) não pode chamar
      // /users — evita um 403 inútil no console/Network (o App já mostra a tela
      // HubAccessDenied nesse caso).
      if (user && user.access_hub_nfse === false) return;
      if (user && (user.role === "admin" || user.role === "normal")) {
        try {
          const response = await usersAPI.getAll();
          if (response.success) {
            setUsers(response.data.users);
          }
        } catch (error) {
          console.error("Error loading users:", error);
        }
      }
    };

    loadUsers();
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);

      if (response.success) {
        const userData = response.data.user;
        const token = response.data.token;

        // Store user data with token
        const userWithToken = { ...userData, token };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userWithToken));

        return { success: true, user: userData };
      }

      return { success: false, message: response.message };
    } catch (error) {
      const errorMessage = handleAPIError(error);
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setUsers([]);
    localStorage.removeItem("user");

    // Call logout endpoint (optional, for logging purposes)
    authAPI.logout().catch(console.error);
  };

  const createUser = async (userData) => {
    try {
      const response = await usersAPI.create(userData);

      if (response.success) {
        // Refresh users list
        const usersResponse = await usersAPI.getAll();
        if (usersResponse.success) {
          setUsers(usersResponse.data.users);
        }

        return { success: true, user: response.data.user };
      }

      return { success: false, message: response.message };
    } catch (error) {
      const errorMessage = handleAPIError(error);
      return { success: false, message: errorMessage };
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const response = await usersAPI.update(userId, userData);

      if (response.success) {
        // Update users list
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.id === userId ? response.data.user : u))
        );

        // Update current user if editing own profile
        if (user && user.id === userId) {
          setUser(response.data.user);

          // Update localStorage
          const savedUser = localStorage.getItem("user");
          if (savedUser) {
            const userWithToken = JSON.parse(savedUser);
            localStorage.setItem(
              "user",
              JSON.stringify({
                ...response.data.user,
                token: userWithToken.token,
              })
            );
          }
        }

        return { success: true, user: response.data.user };
      }

      return { success: false, message: response.message };
    } catch (error) {
      const errorMessage = handleAPIError(error);
      return { success: false, message: errorMessage };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await usersAPI.delete(userId);

      if (response.success) {
        // Remove user from list
        setUsers((prevUsers) => prevUsers.filter((u) => u.id !== userId));
        return { success: true };
      }

      return { success: false, message: response.message };
    } catch (error) {
      const errorMessage = handleAPIError(error);
      return { success: false, message: errorMessage };
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      const response = await usersAPI.toggleStatus(userId);

      if (response.success) {
        // Update users list
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.id === userId ? { ...u, status: response.data.status } : u
          )
        );

        return { success: true, status: response.data.status };
      }

      return { success: false, message: response.message };
    } catch (error) {
      const errorMessage = handleAPIError(error);
      return { success: false, message: errorMessage };
    }
  };

  const hasPermission = (requiredRole) => {
    if (!user) return false;

    const roleHierarchy = {
      cliente: 1,
      normal: 2,
      admin: 3,
    };

    return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
  };

  const value = {
    user,
    users,
    loading,
    login,
    logout,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
